import Util from '../util/Util';
import FocusManagement from '../util/FocusManagement';

/**
 * Manages the various aria- properties for an expansion as well as any required class changes.
 *
 * @param args object with the properties {
 *   toggleSelector: The element that acts as a toggle.
 *      Attributes: aria-controls and aria-expanded
 *      Events: click
 * 
 *   targetSelector: The element being expanded
 *      Classes: targetExpandedClass (default 'expanded')
 *      Used as the default element for other properties when not present
 *
 *   targetContentSelector: Content of the target (default: target)
 *      Attributes: aria-hidden
 *      All focusables within the targetContent are disabled when collapsed
 *
 *   parentSelector: (Default: 'body') A parent of both toggleSelector and targetSelector.
 *      Use parentExpandedClass to add a class showing the expanded state.
 *
 *   additionalToggleSelector: Secondary togglers
 *      Must be children of the parentSelector.
 *      Get the same attributes and event handlers as the primary toggleSelector
 *
 *   onToggle: (Default: none) A function called on expand/contract
 *      function(isExpanded, targetElement).  Called after classList has been set, but before aria attributes
 *      and focusable updates
 * 
 *   expandedClass: (Default: 'expanded') Class for the targetSelector.  
 *      Required, as this is what we inspect to determine whether the target is expanded or not.
 
 *   toggleExpandedClass: (Default: none) Class for the toggleSelector
 *   parentExpandedClass: (Default: none) Class for the parentSelector
 * }
 */
function init(args) {
  const parentSelector = (args.parentSelector || 'body');
  const expandedClass = (args.expandedClass || 'expanded');

  document.querySelectorAll(args.toggleSelector).forEach(toggle => {
    const parent = toggle.closest(parentSelector);
    if (!parent) { return; }

    const target = parent.querySelector(args.targetSelector);
    if (!target) { return; }

    const targetContent = (args.targetContentSelector) ? target.querySelector(args.targetContentSelector) : target;
    if (!targetContent) { return; }
    
    const additionalToggles = (args.additionalToggleSelector) ? parent.querySelectorAll(args.additionalToggleSelector) : [];
    const allToggles = [ toggle ];
    
    additionalToggles.forEach(t => ((t !== toggle) && allToggles.push(t)));

    // Set aria-controls
    if (!target.id) { target.id = Util.uniqueId(); }
    allToggles.forEach(t => t.setAttribute('aria-controls', target.id));
    
    const fnSetExpanded = (isExpanded) => {
      args.toggleExpandedClass && allToggles.forEach(t => t.classList.toggle(args.toggleExpandedClass, isExpanded));
      args.parentExpandedClass && parent.classList.toggle(args.parentExpandedClass, isExpanded);
      target.classList.toggle(expandedClass, isExpanded);
      
      args.onToggle && args.onToggle(isExpanded, target);
      allToggles.forEach(t => t.setAttribute('aria-expanded', (isExpanded ? 'true' : 'false')));
      
      targetContent.setAttribute('aria-hidden', (isExpanded ? 'false' : 'true'));

      FocusManagement.toggleFocusables(targetContent, isExpanded);
    };
    
    // Initialize
    const initExpanded = (toggle.getAttribute('aria-expanded') === 'true') || target.classList.contains(expandedClass);
    fnSetExpanded(initExpanded);
  
    allToggles.forEach(t => {
      t.ExpandToggler = {
        setExpanded: fnSetExpanded
      };
    
      t.addEventListener('click', (e) => {
        e.preventDefault();
        const isExpanded = target.classList.contains(expandedClass);
        fnSetExpanded(!isExpanded);
      })
    });
  });
}

export default { init };